import { useEffect, useState } from "react";
import { Search, KeyRound, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { BigButton, Field, inputCls } from "@/components/ui-kit";

export function PasswordDialog({ target, path, onClose }) {
  const { t } = useApp();
  const [pw, setPw] = useState("");
  useEffect(() => setPw(""), [target]);
  const save = async () => {
    try { await api.post(path, { password: pw }); toast.success(t("password_reset_done")); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="admin-password-dialog">
        <DialogTitle className="font-heading text-xl">{t("reset_password")}</DialogTitle>
        <p className="text-sm text-muted-foreground" dir="ltr">{target?.email}</p>
        <Field label={t("new_password")}><input type="text" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} dir="ltr" data-testid="admin-new-password-input" /></Field>
        <BigButton disabled={pw.length < 8} onClick={save} data-testid="admin-password-save"><KeyRound className="w-5 h-5" />{t("reset_password")}</BigButton>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersTab() {
  const { t, lang } = useApp();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [pwFor, setPwFor] = useState(null);
  const load = (query) => api.get("/admin/users", { params: { q: query } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { const id = setTimeout(() => load(q), 300); return () => clearTimeout(id); }, [q]);
  const toggle = async (u) => {
    try { await api.post(`/admin/users/${u.id}/disable`, { disabled: !u.disabled }); load(q); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="admin-users-tab">
      <div className="relative">
        <Search className="w-5 h-5 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_users")} className={`${inputCls} ps-10`} data-testid="admin-users-search" />
      </div>
      <div className="rounded-xl bg-white border border-border card-soft">
        <table className="w-full text-xs sm:text-sm table-fixed">
          <thead className="text-muted-foreground"><tr>{["email", "name", "shop_name", "created", "status", ""].map((k, i) => <th key={i} className="px-2 py-2 text-start font-medium">{k && t(k)}</th>)}</tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid="admin-user-row">
                <td className="px-2 py-2 font-semibold break-all" dir="ltr">{u.email}</td><td className="p-3">{u.name}</td><td className="p-3">{u.shop_name}</td>
                <td className="px-2 py-2 text-muted-foreground">{fmtDate(u.created_at, lang)}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${u.disabled ? "bg-destructive/10 text-destructive" : "bg-[#1F9D55]/10 text-[#1F9D55]"}`} data-testid="admin-user-status">{t(u.disabled ? "disabled_label" : "enabled_label")}</span></td>
                <td className="p-3">
                  <span className="flex gap-1.5 justify-end">
                    <button type="button" onClick={() => toggle(u)} className="h-9 px-3 rounded-lg border border-border text-xs font-bold flex items-center gap-1 whitespace-nowrap" data-testid="admin-user-toggle">
                      {u.disabled ? <CheckCircle2 className="w-4 h-4 text-[#1F9D55]" /> : <Ban className="w-4 h-4 text-destructive" />}{t(u.disabled ? "enable_login" : "disable_login")}
                    </button>
                    <button type="button" onClick={() => setPwFor(u)} className="h-9 px-3 rounded-lg bg-[#0B2A5B] text-white text-xs font-bold flex items-center gap-1 whitespace-nowrap" data-testid="admin-user-reset-pw"><KeyRound className="w-4 h-4" />{t("reset_password")}</button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PasswordDialog target={pwFor} path={pwFor ? `/admin/users/${pwFor.id}/password` : ""} onClose={() => setPwFor(null)} />
    </div>
  );
}
